import { useMutation, useQueryClient, type QueryKey, type UseMutationOptions, type UseMutationResult } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';
import { useEffectiveClientId } from '@/contexts/AdminClientsContext';

/** Options for {@link useModuleMutation}. */
export type ModuleMutationOptions<TData, TVariables> = Omit<
  UseMutationOptions<TData, Error, TVariables>,
  'mutationFn'
> & {
  /** Performs the relay request; its response body becomes the mutation result. */
  fn: (variables: TVariables) => Promise<AxiosResponse<TData>>;
  /**
   * Query keys to invalidate after a successful request, built from the
   * effective client id, the response body and the mutation variables.
   */
  invalidate?: (clientId: string | null, data: TData, variables: TVariables) => QueryKey[];
};

/**
 * `useMutation` for module relay endpoints. Unwraps `response.data` and
 * invalidates the keys returned by `invalidate` on success, before any
 * caller-provided `onSuccess` runs.
 */
export function useModuleMutation<TData, TVariables = void>({
  fn,
  invalidate,
  onSuccess,
  ...options
}: ModuleMutationOptions<TData, TVariables>): UseMutationResult<TData, Error, TVariables> {
  const queryClient = useQueryClient();
  const clientId = useEffectiveClientId();

  return useMutation({
    ...options,
    mutationFn: async (variables: TVariables) => {
      const response = await fn(variables);
      return response.data;
    },
    onSuccess: (data, variables, onMutateResult, context) => {
      for (const queryKey of invalidate?.(clientId, data, variables) ?? []) {
        void queryClient.invalidateQueries({ queryKey });
      }
      return onSuccess?.(data, variables, onMutateResult, context);
    },
  });
}
